import { useState, useEffect, useRef, type ReactNode } from "react";
import logo from "@/assets/gate-logo.jpg";
import "./GateIntro.css";

/**
 * GateIntro
 * A first-load cinematic: two bronze-framed doors carrying the Fine Arts logo
 * swing open toward the viewer while the camera pushes through into the site.
 * Plays once per browser session (sessionStorage), then renders children.
 */
export default function GateIntro({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<"intro" | "playing" | "done">(() => {
    if (typeof window !== "undefined" && window.sessionStorage.getItem("fa_gate_seen") === "1") {
      return "done";
    }
    return "intro";
  });
  const startedRef = useRef(false);

  // Lock scroll while the gate is on screen
  useEffect(() => {
    if (phase !== "done") {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [phase]);

  const open = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    setPhase("playing");
    // total timeline ~6s; reveal the site underneath as the camera passes through
    window.setTimeout(() => {
      try {
        window.sessionStorage.setItem("fa_gate_seen", "1");
      } catch {
        /* ignore */
      }
      setPhase("done");
    }, 6200);
  };

  // Auto-begin shortly after load so it feels like an intro, not a wall.
  useEffect(() => {
    if (phase === "intro") {
      const t = window.setTimeout(open, 1100);
      return () => window.clearTimeout(t);
    }
  }, [phase]);

  return (
    <>
      {children}
      {phase !== "done" && (
        <div className={`fa-gate ${phase === "playing" ? "is-open" : ""}`} aria-hidden="true">
          <div className="fa-gate__world">
            {/* glow seen through the opening */}
            <div className="fa-gate__beyond" />
            <div className="fa-gate__rays" />

            <div className="fa-gate__wall" />

            <div className="fa-gate__doors">
              {/* LEFT DOOR */}
              <div className="fa-door fa-door--left">
                <div className="fa-door__edge" />
                <div className="fa-door__face">
                  <div className="fa-door__logo" style={{ backgroundImage: `url(${logo})` }} />
                  <div className="fa-door__shade" />
                </div>
              </div>
              {/* RIGHT DOOR */}
              <div className="fa-door fa-door--right">
                <div className="fa-door__edge" />
                <div className="fa-door__face">
                  <div className="fa-door__logo" style={{ backgroundImage: `url(${logo})` }} />
                  <div className="fa-door__shade" />
                </div>
              </div>
            </div>

            <div className="fa-gate__seam" />
            <div className="fa-gate__seamGlow" />
            <div className="fa-gate__frame" />
            <div className="fa-gate__vignette" />
          </div>
        </div>
      )}
    </>
  );
}
